/**
 * ContractorBadges Component
 * 
 * Displays earned contractor badges with icons and tooltips.
 * Used in: Contractor Profile, Contractor Dashboard, Homeowner-facing contractor cards
 */

import { 
  Star, ThumbsUp, CheckCircle2, ShieldCheck, Shield, FileCheck,
  Award, HelpCircle
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "./ui/tooltip";

// Badge icon mapping
const BADGE_ICONS = {
  top_rated: Star,
  highly_rated: ThumbsUp,
  reliable: CheckCircle2,
  background_verified: ShieldCheck,
  insured: Shield,
  w9_on_file: FileCheck,
};

// Badge color schemes
const BADGE_COLORS = {
  top_rated: "bg-gradient-to-r from-yellow-400 to-amber-500 text-white",
  highly_rated: "bg-gradient-to-r from-blue-400 to-blue-500 text-white",
  reliable: "bg-gradient-to-r from-green-400 to-green-500 text-white",
  background_verified: "bg-gradient-to-r from-purple-400 to-purple-500 text-white",
  insured: "bg-gradient-to-r from-teal-400 to-teal-500 text-white",
  w9_on_file: "bg-gradient-to-r from-indigo-400 to-indigo-500 text-white",
};

// Badge definitions (mirrors backend)
const BADGE_DEFINITIONS = {
  top_rated: {
    name: "Top Rated",
    description: "Avg rating 4.7+ (10+ rated jobs)",
    priority: 1,
  },
  highly_rated: {
    name: "Highly Rated",
    description: "Avg rating 4.5+ (5+ rated jobs)",
    priority: 2,
  },
  reliable: {
    name: "Reliable",
    description: "High completion rate on contracted jobs",
    priority: 3,
  },
  background_verified: {
    name: "Background Check",
    description: "Background check verified",
    priority: 4,
  },
  insured: {
    name: "Insured",
    description: "Insurance verified",
    priority: 5,
  },
  w9_on_file: {
    name: "W9 On File",
    description: "W-9 on file",
    priority: 6,
  },
};

/**
 * Single Badge Component
 */
export function Badge({ badgeId, size = "md", showLabel = true }) {
  const badgeDef = BADGE_DEFINITIONS[badgeId];
  const IconComponent = BADGE_ICONS[badgeId] || Award;
  const colorClass = BADGE_COLORS[badgeId] || "bg-gray-500 text-white";
  
  if (!badgeDef) return null;
  
  const sizeClasses = {
    sm: "w-6 h-6 text-xs",
    md: "w-8 h-8 text-sm",
    lg: "w-10 h-10 text-base",
  };
  
  const iconSizes = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`inline-flex items-center gap-1.5 cursor-help`}
            data-testid={`badge-${badgeId}`}
          >
            <div className={`${sizeClasses[size]} ${colorClass} rounded-full flex items-center justify-center shadow-sm`}>
              <IconComponent className={iconSizes[size]} />
            </div>
            {showLabel && (
              <span className="text-sm font-medium text-gray-700">{badgeDef.name}</span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">{badgeDef.name}</p>
          <p className="text-xs text-gray-400">{badgeDef.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Badge Row Component - displays multiple badges
 */
export function BadgeRow({ badges = [], maxBadges = null, size = "md", showLabels = true, emptyMessage = null }) {
  // Sort badges by priority
  const sortedBadges = [...badges].sort((a, b) => {
    const priorityA = BADGE_DEFINITIONS[a]?.priority || 999;
    const priorityB = BADGE_DEFINITIONS[b]?.priority || 999;
    return priorityA - priorityB;
  });
  
  // Limit badges if maxBadges specified
  const displayBadges = maxBadges ? sortedBadges.slice(0, maxBadges) : sortedBadges;
  const hiddenCount = maxBadges && sortedBadges.length > maxBadges ? sortedBadges.length - maxBadges : 0;
  
  if (displayBadges.length === 0) {
    if (emptyMessage) {
      return (
        <div className="flex items-center gap-2 text-gray-400 text-sm" data-testid="no-badges">
          <HelpCircle className="w-4 h-4" />
          <span>{emptyMessage}</span>
        </div>
      );
    }
    return null;
  }
  
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="badge-row">
      {displayBadges.map((badgeId) => (
        <Badge key={badgeId} badgeId={badgeId} size={size} showLabel={showLabels} />
      ))}
      {hiddenCount > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-gray-400 cursor-help">+{hiddenCount} more</span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">
                {sortedBadges.slice(maxBadges).map(b => BADGE_DEFINITIONS[b]?.name).join(", ")}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/**
 * Badge Section Component - for profile pages
 */
export function BadgeSection({ badges = [], title = "Badges" }) {
  return (
    <div className="space-y-3" data-testid="badge-section">
      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
        <Award className="w-5 h-5 text-[#0FA3B1]" />
        {title}
      </h3>
      
      {badges.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {badges.map((badgeId) => (
            <Badge key={badgeId} badgeId={badgeId} size="md" showLabel={true} />
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-4 text-center" data-testid="no-badges-message">
          <HelpCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">
            No badges yet — complete jobs and get rated to earn badges.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Compact Badge Display - for cards with limited space
 */
export function CompactBadges({ badges = [], maxBadges = 2 }) {
  const sortedBadges = [...badges].sort((a, b) => {
    const priorityA = BADGE_DEFINITIONS[a]?.priority || 999;
    const priorityB = BADGE_DEFINITIONS[b]?.priority || 999;
    return priorityA - priorityB;
  });
  
  const displayBadges = sortedBadges.slice(0, maxBadges);
  
  if (displayBadges.length === 0) return null;
  
  return (
    <TooltipProvider>
      <div className="flex items-center gap-1" data-testid="compact-badges">
        {displayBadges.map((badgeId) => {
          const IconComponent = BADGE_ICONS[badgeId] || Award;
          const colorClass = BADGE_COLORS[badgeId] || "bg-gray-500 text-white";
          const badgeDef = BADGE_DEFINITIONS[badgeId];
          
          return (
            <Tooltip key={badgeId}>
              <TooltipTrigger asChild>
                <div className={`w-5 h-5 ${colorClass} rounded-full flex items-center justify-center cursor-help`}>
                  <IconComponent className="w-3 h-3" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs font-medium">{badgeDef?.name}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

export default BadgeRow;
